import axios from "axios";
import axiosRetry from "axios-retry";

const requestTimedOut = error => error.code === "ECONNABORTED";

const getServerRequestInterceptor = ({ logger }) => config => {
  config.timing = {
    start: Date.now()
  };

  logger.http("Request", {
    url: config.url,
    method: config.method,
    headers: config.headers,
    serverUuid: config.asosConfig.serverUuid
  });
  return config;
};

function getServerResponseInterceptor({ serverRequestLogger, logger }) {
  const logRequest = (response, params = {}) => {
    const { start } = response.config.timing;
    const timeTaken = Date.now() - start;

    if (serverRequestLogger) {
      serverRequestLogger({
        ...response.config.asosConfig,
        "request.url": response.config.url,
        timeTaken,
        ...params
      });
    }
    logger.http("Response", {
      ...response.config.asosConfig,
      ...params,
      url: response.config.url,
      method: response.config.method,
      headers: response.headers,
      timeTaken
    });
  };

  const handleResponse = response => {
    logRequest(response, {
      "response.status": response.status,
      httpResponseMessage: response.statusText
    });
    return response;
  };

  const handleResponseFailure = error => {
    if (!axios.isCancel(error)) {
      logRequest(error, {
        errorCode: error.code,
        errorMessage: error.toString()
      });
    }
    return Promise.reject(error);
  };

  return [handleResponse, handleResponseFailure];
}

const getClientRequestInterceptor = () => request => request;

const getClientResponseInterceptor = () => [
  response => response,
  error => Promise.reject(error)
];

const getRequestInterceptor = ({ logger }) =>
  SERVER
    ? getServerRequestInterceptor({ logger })
    : getClientRequestInterceptor();

const getResponseInterceptor = ({ serverRequestLogger, logger }) =>
  SERVER
    ? getServerResponseInterceptor({ serverRequestLogger, logger })
    : getClientResponseInterceptor();

export default function enhanceAxios({
  serverRequestLogger,
  retries,
  retryDelay,
  logger
} = {}) {
  const axiosInstance = axios.create();

  axiosInstance.interceptors.request.use(getRequestInterceptor({ logger }));
  axiosInstance.interceptors.response.use(
    ...getResponseInterceptor({ serverRequestLogger, logger })
  );

  axiosRetry(axiosInstance, {
    retries,
    retryDelay,
    retryCondition: error => !requestTimedOut(error)
  });

  return axiosInstance;
}
